import React from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import useYupValidationResolver from 'hook/useYupValidationResolver';

import { ShadowCompany } from 'features/companies/components/ShadowCompany';

import { AddButton } from 'components/Button';
import { ListCard, PaperCard } from 'components/Card';
import { CardAutocomplete, FormProvider, TextField } from 'components/Form';
import { IconWrapper } from 'components/Icon';

import { getCompanies } from '../api/getCompanies';
import { useGetByID } from '../api/getById';
import { useAddCompanies } from '../api/addCompany';
import { supplierConsortiumSchema } from '../validation';
import { useCreateConsortiumGroup } from '../api/createConsortiumGroup';

const ItemCard = ({
  Name: name,
  VAT: vatNumber,
  Description: description,
  companyType,
  alreadySelected,
  alreadyAdded,
  isConsortiumGroup,
  company_id,
  ...rest
}) => {
  if (!isConsortiumGroup && alreadyAdded.some(item => item.company_id === company_id)) {
    return null;
  }

  if (alreadySelected.some(item => item.company_id === company_id)) {
    return null;
  }

  return (
    <Stack gap={2} alignItems={'flex-start !important'} {...rest}>
      <Typography variant="h5" fontWeight="bold">
        {name}
      </Typography>

      <Stack gap={0.5} alignItems={'start'}>
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          VAT Number
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {vatNumber}
        </Typography>
      </Stack>

      <Stack gap={0.5} alignItems={'start'}>
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          Description
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          {description}
        </Typography>
      </Stack>

      <AddButton onClick={() => null}>Add to {companyType} list</AddButton>
    </Stack>
  );
};
const CustomEndIcon = () => (
  <IconWrapper
    icon="plus"
    sx={{
      height: 24,
      svg: { width: 16, height: 16, color: 'primary.main', stroke: 'white', strokeWidth: 1.33 },
    }}
  />
);

export const AddSuppliersForm = ({ refetch, alreadyAdded }) => {
  const { id: dpsID } = useParams();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isConsortiumGroup, setIsConsortiumGroup] = useState(false);

  const methods = useForm({
    defaultValues: {
      cg_name: '',
    },
    resolver: useYupValidationResolver(
      isConsortiumGroup ? supplierConsortiumSchema : yup.object().shape({})
    ),
  });

  const { data: dps } = useGetByID(dpsID);

  const { mutateAsync: updateCompanies } = useAddCompanies();
  const { mutateAsync: createConsortiumGroup } = useCreateConsortiumGroup();

  const handleOpenModal = useCallback(() => setModalOpen(true), []);
  const handleCloseModal = useCallback(() => setModalOpen(false), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    setSelected([]);
  }, []);

  const handleIsConsortiumGroupChange = (_, isConsortiumGroup) => {
    if (!isConsortiumGroup)
      methods.reset({ cg_name: '' }, { keepDirty: false, keepErrors: false, keepIsValid: false });

    setIsConsortiumGroup(isConsortiumGroup);
  };

  const onUpdate = () => {
    refetch();
    handleClose();
  };

  const onSubmit = async ({ cg_name }) => {
    if (!isConsortiumGroup)
      updateCompanies({
        dps_id: dpsID,
        companies_ids: selected.map(({ company_id }) => +company_id),
        company_type: 'supplier',
      })
        .then(onUpdate)
        .catch(() => null);
    else {
      createConsortiumGroup({
        dps_id: dpsID,
        cg_name,
        companies_ids: selected.map(({ company_id }) => +company_id),
      })
        .then(onUpdate)
        .catch(() => null);
    }
  };

  const removeSelected = (id) =>
    setSelected((selected) => selected.filter(({ company_id }) => company_id !== id));

  const addCompany = (value) => {
    if (value) setSelected((previousSelected) => [...previousSelected, value]);
  };

  const addShadowSupplier = (shadowSupplier) => {
    setSelected((previousSelected) => [...previousSelected, shadowSupplier]);
  };

  return (
    <>
      <AddButton
        fullWidth
        CustomEndIcon={CustomEndIcon}
        onClick={() => setOpen(true)}
        sx={{
          svg: { mt: -1 },
        }}
      >
        Add suppliers
      </AddButton>
      {ReactDOM.createPortal(
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          background: '#F3F4F6',
          display: open ? 'block' : 'none',
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        <ShadowCompany
          open={modalOpen}
          handleClose={handleCloseModal}
          addToList={addShadowSupplier}
          type="supplier"
        />

        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Stack width={1} height={1}>
            <PaperCard
              sx={{
                background: '#fff',
                width: '100%',
                py: 4,
                px: 8,
                border: 0,
                borderRadius: 0,
                boxShadow: 26,
                display: 'flex',
                boxSizing: 'border-box',
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              <Stack direction="row" width={1} alignItems="center" justifyContent="space-between">
                <Typography variant="h3">
                  Add suppliers to ‘{dps?.dps_name || 'the DPS'}’
                </Typography>
                <Stack direction="row" gap={2}>
                  <Button
                    size="medium"
                    onClick={handleClose}
                    variant="contained"
                    sx={({ palette }) => ({
                      width: 82,
                      boxShadow: 25,
                      color: palette.primary.main,
                      backgroundColor: '#fff',
                    })}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!selected.length}
                    size="medium"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ width: 67 }}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            </PaperCard>

            <Stack direction="row" flex={1}>
              <Stack mt={7} px={8} gap={3} flex={1}>
                <Stack gap={3}>
                  <Typography variant="h2">Consortium group</Typography>
                  <Stack gap={0}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={isConsortiumGroup}
                            onChange={handleIsConsortiumGroupChange}
                          />
                        }
                        label="Adding new consortium group?"
                      />
                    </FormGroup>

                    <TextField
                      disabled={!isConsortiumGroup}
                      name="cg_name"
                      label="Consortium Group Name"
                      placeholder="Please enter Consortium Group name"
                      required={true}
                      type="text"
                      sx={{ mb: 2 }}
                      fullWidth
                    />
                  </Stack>
                </Stack>

                <Typography variant="h2">Select suppliers</Typography>
                <CardAutocomplete
                  label={`Existing suppliers`}
                  fullWidth
                  onChange={addCompany}
                  queryFunction={(searchTerm) => getCompanies('supplier', searchTerm)}
                  ItemCard={(props) => <ItemCard companyType={'supplier'} {...props} isConsortiumGroup={isConsortiumGroup} alreadyAdded={alreadyAdded} alreadySelected={selected} />}
                  handleNotFoundButtonClick={handleOpenModal}
                  optionLabelName="company_id"
                />

                <AddButton onClick={handleOpenModal}>Create shadow profile</AddButton>
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ borderColor: 'grey.500' }} />

              <Stack mt={7} gap={3} flex={1} px={8}>
                <Typography variant="h2">
                  Added suppliers {selected.length ? `(${selected.length})` : ''}
                </Typography>

                {selected.length !== 0 && (
                  <Stack gap={1}>
                    <Typography>These suppliers can shop from this framework agreement</Typography>

                    {selected.map(
                      ({ Name: name, VAT: vatNumber, Type: type, company_id }, index) => (
                        <ListCard key={index}>
                          <Stack direction="row" gap={1} flex={4} alignItems="center">
                            <Typography variant="body1" fontWeight="bold">
                              {name}
                            </Typography>
                            <Chip
                              label={type}
                              sx={{
                                px: 1,
                                py: 0.5,
                                fontWeight: 'bold',
                                border: '1px solid white',
                                boxShadow: 25,
                                fontSize: '0.75rem',
                              }}
                            />
                          </Stack>

                          <Stack direction="row" flex={3} gap={3} alignItems="center">
                            <Stack>
                              <Typography variant="caption" color="text.secondary">
                                VAT Number
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {vatNumber}
                              </Typography>
                            </Stack>

                            <Button
                              color="primary"
                              variant="contained"
                              onClick={() => removeSelected(company_id)}
                            >
                              Remove from list
                            </Button>
                          </Stack>
                        </ListCard>
                      )
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </FormProvider>
      </Box>,
      document.body
      )}
    </>
  );
};
